// Singleton Amplitude Experiment client.
//
// One source of truth for variants. Read from:
//   - React (via the provider in react.tsx)
//   - the Amplitude Analytics enrichment plugin (enrichment.ts)
//   - the Meta CAPI passthrough (tracking.ts)
//
// Lifecycle:
//   - initExperimentClient() is called once at app boot from src/integrations.js,
//     immediately after amplitude.init(). It kicks off experiment.start() which
//     fetches local flag configs.
//   - During the brief window between init and start completion, getVariant()
//     returns 'off' (treated as fallback). After start resolves, variants are live.
//   - QA overrides (?exp_override=key:variant) are applied at read time, shadowing
//     the SDK assignment. Overrides skip exposure tracking so QA traffic doesn't
//     pollute Amplitude analysis.

import { Experiment, type ExperimentClient, type Variant } from '@amplitude/experiment-js-client';
import { defaultAllowed, readOverrides } from './override';

export interface ExperimentInitOptions {
  /** Amplitude Experiment deployment key (different from the Analytics API key). */
  deploymentKey: string;
  /** 'EU' | 'US'. Mirrored from Amplitude Analytics config. */
  serverZone: 'EU' | 'US';
  /** Bucketing identity — matches deviceId/userId used for Analytics. */
  deviceId: string;
  userId?: string;
  /** Optional user properties used by Amplitude targeting rules. */
  userProperties?: Record<string, string | number | boolean | Array<string | number | boolean>>;
  /** Hard timeout for the initial fetch. Falls through to fallback variants if exceeded. */
  fetchTimeoutMillis?: number;
  /** Build-time environment string ('production' | 'preview' | 'development'). */
  environment?: string;
}

interface ClientState {
  client: ExperimentClient | null;
  ready: boolean;
  overridesAllowed: boolean;
  startPromise: Promise<void> | null;
  /**
   * Flag keys for which the SDK has actually fired `$exposure` for this user
   * (i.e. `getVariant` returned a real SDK-assigned variant — not an override,
   * not the pre-ready fallthrough, not the 'off' fallback for an unloaded
   * config). Drives `getActiveVariants` so event-property tagging mirrors
   * exposure: users on funnels that never mount a flag's surface no longer
   * get their events tagged with that flag's locally-evaluated assignment.
   */
  exposedFlags: Set<string>;
}

const state: ClientState = {
  client: null,
  ready: false,
  overridesAllowed: true,
  startPromise: null,
  exposedFlags: new Set<string>(),
};

const listeners = new Set<() => void>();

/**
 * Idempotent. Safe to call multiple times — only the first call constructs
 * the client and initiates fetch. Subsequent calls return the same Promise.
 */
export function initExperimentClient(opts: ExperimentInitOptions): Promise<void> {
  if (state.startPromise) return state.startPromise;
  state.overridesAllowed = defaultAllowed(opts.environment);

  const client = Experiment.initializeWithAmplitudeAnalytics(opts.deploymentKey, {
    serverZone: opts.serverZone,
    fetchOnStart: true,
    fetchTimeoutMillis: opts.fetchTimeoutMillis ?? 1000,
    automaticExposureTracking: true,
    retryFetchOnFailure: true,
    // Keep the SDK quiet in production; debug logs flow through Amplitude itself.
    debug: opts.environment !== 'production',
  });
  state.client = client;

  state.startPromise = client
    .start({
      user_id: opts.userId ?? opts.deviceId,
      device_id: opts.deviceId,
      user_properties: opts.userProperties,
    })
    .then(() => {
      state.ready = true;
      notify();
    })
    .catch((e: unknown) => {
      // Fail open — components fall through to control. Surface to console for
      // debugging, but never crash the funnel.
      // eslint-disable-next-line no-console
      console.warn('[experiments] start() failed; falling back to control', e);
      state.ready = true;
      notify();
    });

  return state.startPromise;
}

/** True once start() has resolved (or failed; the SDK fails open). */
export function isExperimentClientReady(): boolean {
  return state.ready;
}

/**
 * Resolves once start() has settled, or after `timeoutMillis` — whichever is
 * first. Never rejects. Resolves immediately when the client was never
 * initialized (no deployment key), so callers fall straight through to control.
 * For imperative, non-React callers (e.g. offering selection) that must read a
 * variant before acting on it.
 */
export function whenExperimentsReady(timeoutMillis = 1500): Promise<void> {
  if (state.ready || !state.startPromise) return Promise.resolve();
  return Promise.race([
    state.startPromise.catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMillis)),
  ]);
}

export function getExperimentClient(): ExperimentClient | null {
  return state.client;
}

function notify(): void {
  for (const l of listeners) l();
}

export function subscribeToExperiments(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readVariantOverride(flagKey: string): string | null {
  const overrides = state.overridesAllowed
    ? readOverrides({
        document: typeof document !== 'undefined' ? document : undefined,
        allowed: state.overridesAllowed,
      })
    : {};
  return overrides[flagKey] ?? null;
}

/**
 * Read a single variant, applying QA overrides if present. Triggers exposure
 * on the SDK only when the variant comes from the real assignment (NOT when
 * shadowed by an override).
 *
 * Records the flag in `state.exposedFlags` on real SDK assignments so the
 * enrichment + CAPI layers can scope event-property tagging to exposed flags
 * only. The add() happens BEFORE the SDK call so the synchronous plugin
 * pipeline that runs when `$exposure` fires can see the flag in
 * `getActiveVariants()` — i.e. the exposure event itself carries its own
 * `event_properties.experiments.<flag>` entry. If the SDK returns 'off' or
 * a missing variant (no flag config loaded), we roll the entry back.
 */
export function getVariant(flagKey: string): Variant {
  const override = readVariantOverride(flagKey);
  if (override) return { value: override };
  if (!state.client || !state.ready) return { value: 'off' };
  state.exposedFlags.add(flagKey);
  const variant = state.client.variant(flagKey) ?? { value: 'off' };
  if (!variant.value || variant.value === 'off') {
    state.exposedFlags.delete(flagKey);
  }
  return variant;
}

/**
 * Pure read of the current assignment without triggering exposure. Used by the
 * React layer during render so StrictMode / re-renders don't cause side effects.
 */
export function peekVariant(flagKey: string): Variant {
  const override = readVariantOverride(flagKey);
  if (override) return { value: override };
  if (!state.client || !state.ready) return { value: 'off' };
  return state.client.all()[flagKey] ?? { value: 'off' };
}

/**
 * Snapshot of variants the user has been EXPOSED to, filtered to exclude
 * `'off'` / falsy values. Used by the enrichment plugin + CAPI passthrough
 * to tag events with `experiments.<flag>` only after `$exposure` has fired
 * for that flag.
 *
 * Returns a plain object — safe to spread into `event_properties.experiments`.
 *
 * Why filter by exposure: local-eval assigns every user a variant for every
 * flag at `start()` time, including flags whose surfaces the user never
 * reaches. Tagging events with those un-exposed assignments breaks any
 * event-property segmentation that follows the documented `event_properties
 * .experiments.<flag>` pattern (e.g. a /adhd user would carry the
 * `welcome_2026_headline_v1` variant on every event despite never seeing the
 * /2026 welcome surface). The `exposedFlags` set is populated by `getVariant`
 * once `$exposure` actually fires.
 *
 * QA overrides do NOT appear here. Overrides intentionally skip `$exposure`
 * (per docs/ab-testing.md §8), and tagging them on events would pollute the
 * same event-property analyses exposure was designed to keep clean. The UI
 * still renders the override variant via `getVariant`; only the analytics
 * tag is withheld.
 */
export function getActiveVariants(): Record<string, string> {
  const out: Record<string, string> = {};

  if (!state.client || !state.ready) return out;
  // `all()` doesn't trigger exposure — the `exposedFlags` filter below is
  // what scopes the result to flags the user has actually seen.
  const all = state.client.all();
  for (const k of state.exposedFlags) {
    const value = all[k]?.value;
    if (typeof value === 'string' && value && value !== 'off') {
      out[k] = value;
    }
  }

  return out;
}

/** Test-only — resets singleton state. */
export function __resetForTests(): void {
  state.client = null;
  state.ready = false;
  state.startPromise = null;
  state.overridesAllowed = true;
  state.exposedFlags.clear();
  listeners.clear();
}
